import { AnyObject } from "@hen/stdlib/types";
import {
  CellValueChangedEvent,
  RowNode as AgRowNode,
  CheckboxSelectionCallbackParams,
  ProcessCellForExportParams,
} from "ag-grid-community";
import { ColDef, GridReadyEvent, ValueGetterParams } from "ag-grid-enterprise";
import { isNil } from "lodash";

import { TableProps } from "components/table";
import { FlumeNode, InputData } from "flume";
import { AgGridRef } from "lib/ag-grid";
import { FlatLevel } from "lib/frontend/cm/pages/reviews/[id]/overview";
import { ConvertWithRatesFn } from "lib/frontend/lib/currency";
import { ColumnConfig, ColumnPinMode } from "lib/shared/table-settings";
import {
  Company as PrismaCompany,
  CompensationApproval,
  CompensationApprovalLevel,
  CompensationApprovalStatus,
  Prisma,
  ProposerBudget,
  Review as PrismaReview,
  ReviewStatus,
  SalaryBenchmarkType,
  SalaryIncomeFrequency,
  SalaryIncreaseType,
  TableSettings as PrismaTableSettings,
  User,
  VariableInputMode,
  EquityGrant,
} from "prisma/cm/client";

import { RunEngineContext } from "./logic/rules-engine/run-engine";
import { Proposal } from "../../../shared/proposals/types";

export enum OutputFormat {
  NOMINAL = "nominal",
  PERCENT = "percent",
}

export type TableSettings = PrismaTableSettings & {
  config: Record<string, ReviewTableSettingsMap[number]>;
};

export type HttpGet = "GET";
export type HttpPost = "POST";
export type HttpPut = "PUT";
export type HttpPatch = "PATCH";
export type HttpDelete = "DELETE";
export type HttpMethod = HttpGet | HttpPost | HttpPut | HttpPatch | HttpDelete;

export type RowNode<T> = AgRowNode & {
  data: T;
};

export type RowNodeFacimile<T> = {
  data: T;
  setDataValue: RowNode<T>["setDataValue"];
};

export type BenchmarkThresholdMin = "MIN";
export type BenchmarkThresholdMid = "MID";
export type BenchmarkThresholdMax = "MAX";
export type BenchmarkThreshold =
  | BenchmarkThresholdMin
  | BenchmarkThresholdMid
  | BenchmarkThresholdMax;

export type ReviewStatusAll = "ALL";
export type ReviewStatusFilter = ReviewStatus | ReviewStatusAll;
export type ReviewStatusMap = {
  [Key in ReviewStatusFilter]: string;
};

export type SalaryBenchmarkTypeMap = {
  [Key in SalaryBenchmarkType]: string;
};

export type SalaryIncreaseTypeMap = {
  [Key in SalaryIncreaseType]: string;
};

export type CompensationApprovalStatusMap = {
  [Key in CompensationApprovalStatus]: string;
};

export type VariableInputModeMap = {
  [Key in VariableInputMode]: string;
};

export type SalaryIncomeFrequencyMap = {
  [Key in SalaryIncomeFrequency]: string;
};

export type ReviewStepProps = {
  review: Review;
};

export type ReviewSetupBudgetProps = {
  review: Review;
  proposerBudgets?: ProposerBudget[];
  approvalLevels?: CompensationApprovalLevel[];
  onDataChanged: (e: CellValueChangedEvent) => void;
  allowEditing: boolean;
};

export type ReviewSetupInProgressDialogProps = {
  review: Review;
  title: string;
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
};

export type ReviewProposerStatusDialogProps = {
  review: Review;
  title: string;
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
};

export type SlopeSection = {
  destination?: string;
  value: string;
  label?: string;
};

export type SlopeSectionProps = {
  setCurrentTab?: (tab: SlopeSection) => void;
};

export type ApproverSectionProps = SlopeSectionProps & {
  review: Review;
  proposerBudgets: ProposerBudgetWithApprovalsProposalsAndGrants[];
  proposals: Proposal[];
  user: UserWithCompany;
  setProposerBudgets?: (proposerBudgets: ProposerBudgetWithApprovalsProposalsAndGrants[]) => void;
};

export type AdminSectionProps = SlopeSectionProps & {
  review: Review;
  levels: FlatLevel[];
  proposerBudgets: (ProposerBudgetWithProposals | ProposerBudgetWithApprovalsAndProposals)[];
  proposals: Proposal[];
  user: UserWithCompany | null;
  gridRef?: AgGridRef;
};

export type AnalyticsSectionProps = SlopeSectionProps & {
  review: Review;
  rowData:
    | ReviewPreviewSummary[]
    | ReviewPreviewBudget[]
    | ReviewPreviewPayEquity[]
    | ProposerBudgetWithProposals[];
  setFilteredProposals: ({ label, proposals }: { label: string; proposals: Proposal[] }) => void;
};

export type ReviewPreviewPayEquity = {
  category: {
    label: string;
    indent?: number;
    proposals?: Proposal[];
  };
  groupBy?: {
    label: string;
    proposals?: Proposal[];
  };
  thenBy?: {
    label: string;
    proposals?: Proposal[];
  };
  headcount: number;
  headcountPercent: number;
  promotions: number;
  promotionsPercent: number;
  averageSalaryIncrease: number;
  compaRatioSalary: { before: number; after: number };
};

export type ReviewPreviewSummary = {
  category: {
    label: string;
    indent?: number;
    proposals?: Proposal[];
  };
  headcount: number;
  current: string;
  change: string;
  changePercent: string;
  future: string;
};

export type ReviewPreviewBudget = {
  type: string;
  proposed: string;
  budget: string;
  percent: string;
};

export type ProposalEligibility = Pick<Proposal, "id" | "eligibleForEquity">;

export type ProposalUpdate = { updatedAt: string } & Partial<
  Pick<
    Proposal,
    | "id"
    | "raise"
    | "raiseInputMode"
    | "raisePercent"
    | "variableTarget"
    | "bonus"
    | "bonusInputMode"
    | "bonusPercent"
    | "justification"
    | "bonusTargetInputMode"
    | "bonusTargetPercent"
    | "bonusTargetNominal"
    | "oteIncrease"
    | "oteIncreasePercent"
    | "oteIncreaseInputMode"
    | "notes"
    | "title"
    | "suggestedRaiseNote"
    | "suggestedBonusNote"
    | "individualAchievementRating"
    | "groupAchievementRating"
  > & {
    levelId: number | null;
  }
>;

export type ProposerBudgetBudgetsUpdate = {
  proposerId: string;
  raiseBudgetInputMode: VariableInputMode;
  bonusBudgetInputMode: VariableInputMode;
  raiseBudget: number | null;
  bonusBudget: number | null;
  equityBudget: number | null;
};

const userWithCompany = Prisma.validator<Prisma.UserArgs>()({
  include: {
    company: true,
    emails: {
      where: { isPrimary: true },
      select: {
        email: true,
        isPrimary: true,
      },
    },
  },
});
const userWithReviewRelations = Prisma.validator<Prisma.UserArgs>()({
  include: {
    proposerBudgets: { select: { review: { select: { name: true } } } },
    directApprovals: { select: { review: { select: { name: true } } } },
    managerApprovals: { select: { review: { select: { name: true } } } },
    hrSupportRecipients: { select: { id: true } },
    emails: { select: { email: true } },
  },
});
const userWithTeamData = Prisma.validator<Prisma.UserArgs>()({
  include: { team: true },
});
const userWithManager = Prisma.validator<Prisma.UserArgs>()({
  include: { manager: true },
});

const levelWithRelations = Prisma.validator<Prisma.CompensationApprovalLevelArgs>()({
  include: {
    approvals: {
      include: {
        proposerBudget: {
          include: {
            proposals: true,
          },
        },
        approver: true,
      },
    },
  },
});
const approvalWithLevel = Prisma.validator<Prisma.CompensationApprovalArgs>()({
  include: {
    approvalLevel: true,
    approver: true,
  },
});

const proposerBudgetWithUsersAndApprovers = Prisma.validator<Prisma.ProposerBudgetArgs>()({
  include: {
    proposer: {
      include: {
        team: true,
      },
    },
    approvals: { include: { approver: true } },
  },
});
const proposerBudgetWithProposals = Prisma.validator<Prisma.ProposerBudgetArgs>()({
  include: {
    proposer: true,
    proposals: {
      include: {
        owner: true,
      },
    },
  },
});
const proposerBudgetWithApprovalsAndProposals = Prisma.validator<Prisma.ProposerBudgetArgs>()({
  include: {
    proposer: true,
    proposals: true,
    approvals: {
      include: {
        approvalLevel: { include: { approver: true } },
        approver: true,
      },
    },
  },
});
const proposerBudgetWithApprovalsProposalsAndGrants = Prisma.validator<Prisma.ProposerBudgetArgs>()(
  {
    include: {
      proposer: true,
      approvals: {
        include: {
          approvalLevel: { include: { approver: true } },
          approver: true,
        },
      },
      proposals: {
        include: {
          range: true,
          owner: { include: { range: true } },
        },
      },
    },
  },
);
const proposerBudgetBudgetData = Prisma.validator<Prisma.ProposerBudgetArgs>()({
  select: {
    proposer: true,
    raiseBudget: true,
    bonusBudget: true,
    proposerId: true,
    id: true,
  },
});

const proposalWithOwner = Prisma.validator<Prisma.ProposalArgs>()({
  include: { owner: true },
});

export type Review = Omit<PrismaReview, "exchangeRateDate"> & {
  exchangeRateDate: Date;
};

export type Company = PrismaCompany;

export type ReviewWithCompany = Review & { company: Company };

export type UserWithCompany = Prisma.UserGetPayload<typeof userWithCompany> & {
  company: Company;
} & { companyId: number };
export type UserWithMaybeCompany = Prisma.UserGetPayload<typeof userWithCompany>;
export type UserWithReviewRelations = Prisma.UserGetPayload<typeof userWithReviewRelations>;
export type UserWithTeamData = Prisma.UserGetPayload<typeof userWithTeamData>;
export type UserWithManager = Prisma.UserGetPayload<typeof userWithManager>;

export type LevelWithRelations = Prisma.CompensationApprovalLevelGetPayload<
  typeof levelWithRelations
> & {
  approvals: (CompensationApproval & {
    approver: User;
    proposerBudget: ProposerBudgetWithProposals;
  })[];
};

export type ApprovalWithLevel = Prisma.CompensationApprovalGetPayload<typeof approvalWithLevel>;

export type ProposalWithOwner = Prisma.ProposalGetPayload<typeof proposalWithOwner> & {
  equityGrants: EquityGrant[];
  owner: Prisma.JsonObject & User;
};

export type ProposerBudgetWithUsersAndApprovers = Prisma.ProposerBudgetGetPayload<
  typeof proposerBudgetWithUsersAndApprovers
>;
export type ProposerBudgetWithProposals = Prisma.ProposerBudgetGetPayload<
  typeof proposerBudgetWithProposals
>;

export type ProposerBudgetWithApprovalsAndProposals = Prisma.ProposerBudgetGetPayload<
  typeof proposerBudgetWithApprovalsAndProposals
>;

export type ProposerBudgetWithApprovalsProposalsAndGrants = Prisma.ProposerBudgetGetPayload<
  typeof proposerBudgetWithApprovalsProposalsAndGrants
>;

export type ProposerBudgetBudgetData = Prisma.ProposerBudgetGetPayload<
  typeof proposerBudgetBudgetData
> & {
  proposerData: Prisma.JsonObject & {
    salaryCurrencyCode: string;
  };
};

export type EnrichedProposal = Proposal & {
  logicSuggestedSalaryIncrease?: number;
  logicSuggestedBonus?: number;
  logicOtherAdjustment?: number;
  logicMarketAdjustment?: number;
  logicMeritAdjustment?: number;
  logicPromotionAdjustment?: number;
};

export type SuggestedNoteTypeRaise = "suggestedRaiseNote";
export type SuggestedNoteTypeBonus = "suggestedBonusNote";
export type SuggestedNoteTypeEquity = "suggestedEquityNote";
export type SuggestedNoteType =
  | SuggestedNoteTypeRaise
  | SuggestedNoteTypeBonus
  | SuggestedNoteTypeEquity;

export type ColumnConfigData = Prisma.JsonObject & {
  hide: boolean;
  headerName: string;
  alwaysHide?: boolean;
  sort?: string;
  pinned?: ColumnPinMode | null;
};

export type ColumnConfigTypePromotions = "promotions";
export type ColumnConfigTypeSalary = "salary";
export type ColumnConfigTypeBonus = "bonus";
export type ColumnConfigTypeEquity = "equity";
export type ColumnConfigTypeSummary = "summary";
export type ColumnConfigTypeOverview = "overview";
export type ColumnConfigTypeAwardLetters = "awardLetters";
export type ColumnConfigType =
  | ColumnConfigTypePromotions
  | ColumnConfigTypeSalary
  | ColumnConfigTypeBonus
  | ColumnConfigTypeEquity
  | ColumnConfigTypeSummary
  | ColumnConfigTypeOverview
  | ColumnConfigTypeAwardLetters;

export type proposerColumnConfig = Prisma.JsonObject &
  Record<string | ColumnConfigType, Record<string, ColumnConfigData>>;

export type SetColumnConfig = React.Dispatch<React.SetStateAction<ReviewTableSettingsMap>>;

export type AwardLetterModePreview = "PREVIEW";
export type AwardLetterModeEmployee = "EMPLOYEE";
export type AwardLetterMode = AwardLetterModePreview | AwardLetterModeEmployee;

export type ReviewTableSettingsMap = Record<
  string, // tableName
  ColumnConfig[]
>;

export type USDollar = "USD";
export type LocalCurrency = "LOCAL_CURRENCY";

export type ShowCol = "show";
export type HideCol = "hide";
export type AlwaysHideCol = "alwaysHide";
export type ForbidCol = "forbid";
export type ColDisplayOptions = ShowCol | HideCol | AlwaysHideCol | ForbidCol;

export type ApprovalTreeNode = { root: CompensationApprovalLevel; parentId: string; depth: number };

export type ApprovalTree = ApprovalTreeNode & {
  children: ApprovalTree[];
};

export type ApprovalTreeWithProposers = ApprovalTreeNode & {
  children: ApprovalTreeWithProposers[];
  proposers: ProposerBudget[] | ProposerBudgetWithProposals[];
};

export type CustomNodeResolver = (
  node: FlumeNode,
  inputValues: InputData,
  context: RunEngineContext,
) => Record<string, unknown>;

export type ProposalWithLogic = Proposal & {
  logicSuggestedSalaryIncrease?: number;
  logicSuggestedBonus?: number;
  logicMarketAdjustment?: number;
  logicMeritIncrease?: number;
  logicPromotionAdjustment?: number;
};

/*
 * Generic typeguard used to determine whether a possibly absent model
 * instance is present. Particularly helpful in optional relations.
 *
 * For example...
 * ```
 * // This will result in a TS error.
 * const { manager } = user;
 * console.log(manager.name);
 *
 * // This will not.
 * const { manager } = user;
 * if !isPresent<User>(manager) return;
 * console.log(manager.name);
 * ```
 *
 */
export const isPresent = <T>(
  possiblyAbsentModelInstance: T | null,
): possiblyAbsentModelInstance is T => {
  return !isNil(possiblyAbsentModelInstance);
};

// kudos https://stackoverflow.com/a/55032655
export type Modify<T, R> = Omit<T, keyof R> & R;

export type CustomCellHeaderData = {
  adminOnly?: boolean;
  onClick?: (context: ProposalTableContext) => void;
  suppressButton?: (review: Review) => boolean;
  buttonLabel?: string;
};

export interface HensColDef<T = any> extends Omit<ColDef<T>, "colId"> {
  colId: string;
  suppressRowClickSelection?: boolean;
  cellExportFormatter?: (params: ProposalColumnFnParams & CellExportParams) => any;
  checkboxSelection?: boolean | ((params: CheckboxSelectionCallbackParams) => boolean);
  customCellHeaderData?: CustomCellHeaderData;
}

export type CellExportParams = ProcessCellForExportParams & {
  formattedValue?: string;
};

export enum ManageUsersModelListMode {
  FAMILY_LEVEL = "familyLevel",
  JOB = "job",
  MISSING_RATINGS = "missingRatings",
  FILTERS = "filters",
  COLUMNS = "columns",
  NONE = "",
}

export enum ManageUsersModelMappingMode {
  FAMILY_LEVEL = "familyLevel",
  JOB = "job",
  NONE = "",
}

export type ModelUiSettings = {
  mappingMode: ManageUsersModelMappingMode;
  updateUserMutation: any; // UseMutationResult<any, any>,
  onCreateRange: any;
  queryClient: any;
  getCompanyQuery: any;
  company: any;
  suppressDefaultSideBarButtons?: boolean;
  allGetModelListQueries?: AnyObject;
};

export type ModelUiOverrides = {
  getColDefs?: (uiSettings: ModelUiSettings) => HensColDef[];
  sideBarButtons?: any;
  uiSettings?: ModelUiSettings;
  onGridReady?: (event: GridReadyEvent) => void;
};

export interface AutoInjectedTableContext {
  convert: ConvertWithRatesFn;
  openModelDrawer: (args: { id: string; modelName: string; action: string }) => void;
}

export type ConfirmationTypePromote = "PROMOTE";
export type ConfirmationTypeEdit = "EDIT";
export type ConfirmationType = ConfirmationTypePromote | ConfirmationTypeEdit;
export const CONFIRMATION_TYPE_PROMOTE: ConfirmationTypePromote = "PROMOTE";
export const CONFIRMATION_TYPE_EDIT: ConfirmationTypeEdit = "EDIT";

export interface ProposalColumnFnParams<T extends any = any> extends ValueGetterParams {
  context: ProposalTableContext & AutoInjectedTableContext;
  data: T;
  value: any;
}

export interface ProposalTableContext {
  review: Review;
  openEditNotesModal?: (
    id: Proposal["id"],
    modalTitle?: string,
    noteCategory?: string,
    onAccept?: () => void,
    onCancel?: () => void,
  ) => void;
  openPromoteModal?: (id: Proposal["id"]) => void;
  setProposalBeingNotated?: (noteData: RowNode<Proposal> | null) => void;
  findProposerBudgetForProposal?: (
    data: Proposal | null,
  ) => ProposerBudgetWithApprovalsProposalsAndGrants | ProposerBudget | null;
  findDirectApprovalForProposal?: (
    data:
      | (Proposal & { proposerBudget: ProposerBudget & { approver: User; proposer: User } })
      | null,
  ) => any;
  proposerBudget?: ProposerBudgetWithApprovalsProposalsAndGrants | null;
  suggestedValuesMap?: any;
  user?: UserWithCompany | null;
  promptForConfirmation?: (type: ConfirmationType, data: RowNode<Proposal>) => void;
  setProposalBeingPromoted?: (proposal: RowNode<Proposal> | null) => void;
  ownerNameOnClick?: (data: ProposalColumnFnParams) => any;
  autofill?: (autofillType: AutofillType) => void;
  convert?: ConvertWithRatesFn;
  uiCurrency?: string;
  disableOpenModelDrawerOnClick?: boolean;
  readOnlyProposerBudgets?: Record<ProposerBudget["id"], boolean>;
  showOutliers?: boolean;
  // presences?: ObjectWithIncludes<
  //   "Presence",
  //   {
  //     user: true;
  //   }
  // >[];
}

export interface ProposalTableProps extends TableProps {
  context: ProposalTableContext;
  suppressOpenDrawerOnClick?: boolean;
  exportFileName?: string;
  review: Review;
}

export type CachedAwardLetter = {
  id: string;
  proposal: Proposal;
  awardLetterUrl: string;
};

export type MetadataEntry = {
  id: string;
  label: string;
};

export enum ProposalStage {
  NOT_SUBMITTED = "NOT_SUBMITTED",
  PENDING_APPROVAL = "PENDING_APPROVAL",
  APPROVED = "APPROVED",
}

export const PROPOSAL_STAGES: ProposalStage[] = [
  ProposalStage.NOT_SUBMITTED,
  ProposalStage.PENDING_APPROVAL,
  ProposalStage.APPROVED,
];

export enum LogicProposalComponent {
  SALARY = "salary",
  BONUS = "bonus",
  BONUS_TARGET = "bonus-target",
  EQUITY = "equity",
  OUTLIERS = "outliers",
  CUSTOM_FIELD = "custom-field",
}

export enum LogicRootNode {
  COMP_SUGGESTION = "runOutput",
  COMP_SUGGESTION_BY_PARTS = "runOutputParts",
  SUGGESTION_RANGE = "runOutputRange",
  BONUS_TARGET = "runOutputBonusTarget",
  OUTLIERS = "outputOutlierChecks",
  EQUITY = "runOutputEquity",
  CUSTOM_FIELD = "runOutputCustomField",
}

export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export type ApprovalLevelWithProposers = {
  approvalLevel: CompensationApprovalLevel;
  proposers: ProposerBudget[];
};

export type CashIncrease = {
  raise?: number;
  oteIncrease?: number;
  bonusTargetNominal?: number;
  bonusTargetPercent?: number;
};

export type FieldTextSetting = {
  id: string;
  fieldId: string;
  label: string;
  description?: string;
  type: string;
  defaultLabel?: string;
  editorSortKey?: string | number;
  explicitKind: "field_text_setting";
};

export type AutofillType = "raise" | "bonus";
